import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, TrendingUp, Users, Scale, ShieldCheck, DollarSign,
  Megaphone, Building2, Briefcase, UserCog, Wallet, FileText, HelpCircle,
  Lightbulb, BarChart3, FileSearch, Receipt, Package, GraduationCap,
  KeyRound, ClipboardList, UserPlus,
  ArrowRight, Sparkles
} from 'lucide-react';

interface DashboardCard {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  bgGradient: string;
  condition?: boolean;
}

export const Dashboard: React.FC = () => {
  const { profile } = useAuth();
  const isOperationsAdmin =
    profile?.role === 'operations_admin' || profile?.role === 'operationsAdmin';
  const isExecutive = profile?.role === 'executive';
  const isPrivileged = isOperationsAdmin || isExecutive;

  // Debug: Log profile to console
  React.useEffect(() => {
    console.log('Dashboard - Profile:', profile);
    console.log('Dashboard - canAccessSales:', profile?.canAccessSales);
    console.log('Dashboard - canAccessHR:', profile?.canAccessHR);
    console.log('Dashboard - canAccessLegal:', profile?.canAccessLegal);
    console.log('Dashboard - canAccessCompliance:', profile?.canAccessCompliance);
    console.log('Dashboard - role:', profile?.role);
  }, [profile]);

  const dashboardCards: DashboardCard[] = [
    {
      to: '/sales',
      title: 'Sales',
      description: 'Manage leads, accounts, and opportunities',
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'text-blue-600',
      bgGradient: 'from-blue-50 to-blue-100',
      condition: profile?.canAccessSales || isPrivileged
    },
    {
      to: '/marketing',
      title: 'Marketing',
      description: 'Campaigns, email marketing, and events',
      icon: <Megaphone className="w-6 h-6" />,
      color: 'text-purple-600',
      bgGradient: 'from-purple-50 to-purple-100',
      condition: profile?.canAccessSales || isPrivileged
    },
    {
      to: '/admin/early-access',
      title: 'Founding Pilot',
      description: 'Pilot waitlist, conversion, and reviews',
      icon: <Sparkles className="w-6 h-6" />,
      color: 'text-amber-600',
      bgGradient: 'from-amber-50 to-amber-100'
    },
    {
      to: '/pilot-crm',
      title: 'Pilot CRM',
      description: 'Manage pilot leads and outreach',
      icon: <ClipboardList className="w-6 h-6" />,
      color: 'text-violet-600',
      bgGradient: 'from-violet-50 to-violet-100'
    },
    {
      to: '/hr',
      title: 'HR',
      description: 'Employee management and onboarding',
      icon: <Users className="w-6 h-6" />,
      color: 'text-green-600',
      bgGradient: 'from-green-50 to-green-100',
      condition: profile?.canAccessHR || isPrivileged
    },
    {
      to: '/payroll',
      title: 'Payroll',
      description: 'Payroll processing and benefits',
      icon: <DollarSign className="w-6 h-6" />,
      color: 'text-emerald-600',
      bgGradient: 'from-emerald-50 to-emerald-100',
      condition: profile?.canAccessHR || isPrivileged
    },
    {
      to: '/legal',
      title: 'Legal',
      description: 'Legal documents and contracts',
      icon: <Scale className="w-6 h-6" />,
      color: 'text-purple-600',
      bgGradient: 'from-purple-50 to-purple-100',
      condition: profile?.canAccessLegal || isPrivileged
    },
    {
      to: '/compliance',
      title: 'Compliance',
      description: 'Compliance tracking and records',
      icon: <ShieldCheck className="w-6 h-6" />,
      color: 'text-orange-600',
      bgGradient: 'from-orange-50 to-orange-100',
      condition: profile?.canAccessCompliance || isPrivileged
    },
    {
      to: '/providers',
      title: 'Providers',
      description: 'Manage provider network and subscriptions',
      icon: <Building2 className="w-6 h-6" />,
      color: 'text-indigo-600',
      bgGradient: 'from-indigo-50 to-indigo-100'
    },
    {
      to: '/employers',
      title: 'Employers',
      description: 'Manage employer accounts and settings',
      icon: <Briefcase className="w-6 h-6" />,
      color: 'text-blue-600',
      bgGradient: 'from-blue-50 to-blue-100'
    },
    {
      to: '/users',
      title: 'Users',
      description: 'Manage all platform users',
      icon: <UserCog className="w-6 h-6" />,
      color: 'text-green-600',
      bgGradient: 'from-green-50 to-green-100'
    },
    {
      to: '/financial',
      title: 'Financial',
      description: 'Track revenue, expenses, and finances',
      icon: <Wallet className="w-6 h-6" />,
      color: 'text-emerald-600',
      bgGradient: 'from-emerald-50 to-emerald-100'
    },
    {
      to: '/financial/funding',
      title: 'Funding Applications',
      description: 'Track and manage funding applications for operations',
      icon: <DollarSign className="w-6 h-6" />,
      color: 'text-blue-600',
      bgGradient: 'from-blue-50 to-blue-100'
    },
    {
      to: '/helpdesk',
      title: 'Helpdesk',
      description: 'Manage support tickets',
      icon: <HelpCircle className="w-6 h-6" />,
      color: 'text-cyan-600',
      bgGradient: 'from-cyan-50 to-cyan-100'
    },
    {
      to: '/feature-requests',
      title: 'Feature Requests',
      description: 'Track and prioritize features',
      icon: <Lightbulb className="w-6 h-6" />,
      color: 'text-pink-600',
      bgGradient: 'from-pink-50 to-pink-100'
    },
    {
      to: '/analytics',
      title: 'Analytics',
      description: 'Business intelligence and insights',
      icon: <BarChart3 className="w-6 h-6" />,
      color: 'text-indigo-600',
      bgGradient: 'from-indigo-50 to-indigo-100'
    },
    {
      to: '/audit-logs',
      title: 'Audit Logs',
      description: 'System activity and security logs',
      icon: <FileSearch className="w-6 h-6" />,
      color: 'text-slate-600',
      bgGradient: 'from-slate-50 to-slate-100'
    },
    {
      to: '/receipt-review',
      title: 'Receipt Review',
      description: 'Review receipts and detect fraud',
      icon: <Receipt className="w-6 h-6" />,
      color: 'text-amber-600',
      bgGradient: 'from-amber-50 to-amber-100'
    },
    {
      to: '/enrollment',
      title: 'Enrollment',
      description: 'Member enrollment and onboarding',
      icon: <UserPlus className="w-6 h-6" />,
      color: 'text-green-600',
      bgGradient: 'from-green-50 to-green-100'
    },
    {
      to: '/password-vault',
      title: 'Password Vault',
      description: 'Secure access to shared credentials',
      icon: <KeyRound className="w-6 h-6" />,
      color: 'text-slate-600',
      bgGradient: 'from-slate-50 to-slate-100'
    },
    {
      to: '/documents',
      title: 'Documents',
      description: 'Centralized document management',
      icon: <FileText className="w-6 h-6" />,
      color: 'text-teal-600',
      bgGradient: 'from-teal-50 to-teal-100'
    },
    {
      to: '/business-documents',
      title: 'Business Docs',
      description: 'Business policies and procedures',
      icon: <Building2 className="w-6 h-6" />,
      color: 'text-sky-600',
      bgGradient: 'from-sky-50 to-sky-100'
    },
    {
      to: '/vendors',
      title: 'Vendors',
      description: 'Vendor and procurement management',
      icon: <Package className="w-6 h-6" />,
      color: 'text-cyan-600',
      bgGradient: 'from-cyan-50 to-cyan-100'
    },
    {
      to: '/team-workspace',
      title: 'Team Workspace',
      description: 'Meetings, tasks, risks, and decisions',
      icon: <Users className="w-6 h-6" />,
      color: 'text-indigo-600',
      bgGradient: 'from-indigo-50 to-indigo-100'
    },
    {
      to: '/training',
      title: 'Training',
      description: 'Training and development',
      icon: <GraduationCap className="w-6 h-6" />,
      color: 'text-pink-600',
      bgGradient: 'from-pink-50 to-pink-100'
    },
    {
      to: '/seed-training-documents',
      title: 'Seed Data',
      description: 'Seed training courses & documents',
      icon: <Sparkles className="w-6 h-6" />,
      color: 'text-blue-600',
      bgGradient: 'from-blue-50 to-blue-100',
      condition: isPrivileged
    },
    {
      to: '/expenses',
      title: 'Expenses',
      description: 'Expense management and reports',
      icon: <Receipt className="w-6 h-6" />,
      color: 'text-emerald-600',
      bgGradient: 'from-emerald-50 to-emerald-100'
    },
    {
      to: '/contracts',
      title: 'Contracts',
      description: 'Contract management and renewals',
      icon: <FileText className="w-6 h-6" />,
      color: 'text-indigo-600',
      bgGradient: 'from-indigo-50 to-indigo-100'
    },
  ].filter(card => card.condition !== false) as DashboardCard[];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-blue-600 to-teal-500 rounded-lg">
              <LayoutDashboard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome, {profile?.firstName}!
              </h1>
              <p className="mt-1 text-gray-600">
                FairCare Operations Portal
              </p>
            </div>
          </div>
        </div>

        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {dashboardCards.map((card) => (
            <Link
              key={card.to}
              to={card.to}
              className="group relative bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg hover:border-gray-200 transition-all duration-200 transform hover:-translate-y-1"
            >
              <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br ${card.bgGradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity`}></div>
              <div className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${card.bgGradient} mb-4 ${card.color} group-hover:scale-110 transition-transform`}>
                {card.icon}
              </div>
              <h3 className={`text-xl font-bold ${card.color} mb-2 group-hover:translate-x-1 transition-transform`}>
                {card.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4">{card.description}</p>
              <div className="flex items-center text-sm font-medium text-gray-500 group-hover:text-blue-600 transition-colors">
                <span>Open</span>
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="mt-8 bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2 text-gray-900">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Quick Stats
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <div className="text-sm font-medium text-gray-500 mb-2">Your Role</div>
              <div className="text-2xl font-bold text-gray-900 capitalize">{profile?.role?.replace('_', ' ') || 'N/A'}</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <div className="text-sm font-medium text-gray-500 mb-2">Department</div>
              <div className="text-2xl font-bold text-gray-900">{profile?.department || 'N/A'}</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <div className="text-sm font-medium text-gray-500 mb-2">Status</div>
              <div className="text-2xl font-bold text-gray-900 capitalize">
                <span className={`inline-flex items-center gap-2`}>
                  <span className={`w-2 h-2 rounded-full ${
                    profile?.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                  }`}></span>
                  {profile?.status || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

